/**
 * Borne d'accueil: the visitor self-registration tablet (spec
 * docs/superpowers/specs/2026-09-27-reception-borne-design.md).
 *
 * Imports come from sibling files, not `./index`, with one exception:
 * `parsePhone`. It is a function DECLARATION, so it is hoisted and safe to
 * reach through the barrel cycle (`index.ts` re-exports this file), as long
 * as it is only called at run time, never at module top level. That is also
 * why `visitorPhone` below is a local transform and not `phoneSchema`:
 * `phoneSchema` is a `const` in `index.ts`, not yet initialised when this
 * module evaluates. `visitor.ts` makes the same trade.
 */
import { z } from "zod/v4";

import { parsePhone } from "./index";
import {
  HOST_SEARCH_MIN_LENGTH,
  spotArrivalPurposeSchema,
} from "./spot-arrival";

export const GATE_DEVICE_KINDS = ["gate", "borne"] as const;
export type GateDeviceKind = (typeof GATE_DEVICE_KINDS)[number];
export const gateDeviceKindSchema = z.enum(GATE_DEVICE_KINDS);

export const BORNE_DISPLAY_MODES = ["complet", "qr"] as const;
export type BorneDisplayMode = (typeof BORNE_DISPLAY_MODES)[number];
export const borneDisplayModeSchema = z.enum(BORNE_DISPLAY_MODES);

/** The display modes an admin may pick. Both, since slice 03 shipped the
 *  rotating code; kept as the one place `approve` (via approveKindError) and
 *  `setDisplayMode` ask, should a mode ever be gated again. */
export function isBorneDisplayModeEnabled(mode: BorneDisplayMode): boolean {
  return BORNE_DISPLAY_MODES.includes(mode);
}

/**
 * Why `gateDevice.approve` must refuse this kind/mode pair, or null. The
 * admin picks the kind, never the device: anything a device says about
 * itself is untrusted (spec §2.1). `targetOrgType` is the organisation the
 * device is being bound to, which Gately staff may pick across tenants, so it
 * is not always the caller's.
 */
export function approveKindError(
  input: { kind: GateDeviceKind; displayMode: BorneDisplayMode | null },
  targetOrgType: string | null,
): string | null {
  if (input.kind === "gate") {
    return input.displayMode === null
      ? null
      : "Le mode d'affichage ne concerne que les bornes d'accueil.";
  }
  if (targetOrgType !== "office") {
    return "Une borne d'accueil ne peut être appairée que dans un immeuble de bureaux.";
  }
  if (input.displayMode === null) {
    return "Choisissez le mode d'affichage de la borne.";
  }
  if (!isBorneDisplayModeEnabled(input.displayMode)) {
    return "Le mode QR n'est pas encore disponible.";
  }
  return null;
}

export const BORNE_SOURCES = ["tablet", "phone"] as const;
export type BorneSource = (typeof BORNE_SOURCES)[number];

export const BORNE_SUBMISSION_STATUSES = [
  "pending",
  "taken",
  "dismissed",
] as const;
export type BorneSubmissionStatus = (typeof BORNE_SUBMISSION_STATUSES)[number];

/** Person search is match-only (spec §4 step 2): at least two LETTERS, never
 *  a browse list, at most five answers. The numbers are slice 01's
 *  `HOST_SEARCH_MIN_LENGTH` / `HOST_SEARCH_MAX_RESULTS`, the one place the
 *  rule lives. On this public device they count letters, not characters: « 12 »
 *  or « a. » must not start a search. */
export function hostQueryLetterCount(q: string): number {
  return (q.match(/\p{L}/gu) ?? []).length;
}

export const borneHostsInputSchema = z.object({
  unitId: z.string().min(1),
  q: z
    .string()
    .trim()
    .max(60)
    .refine((q) => hostQueryLetterCount(q) >= HOST_SEARCH_MIN_LENGTH, {
      message: "Tapez au moins 2 lettres.",
    }),
});

const borneVisitorPhoneSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const e164 = parsePhone(v);
    if (e164 === null) {
      ctx.addIssue({
        code: "custom",
        message: "Numéro de téléphone invalide.",
      });
      return z.NEVER;
    }
    return e164;
  });

/**
 * `borne.submit`'s input, shared by the tablet (slice 02) and the phone
 * (slice 03). `id` is generated by the TABLET when the form opens and sent
 * with every attempt, which makes a retry harmless (spec §3.4); phones omit it.
 * `idNumber` is the whole number as typed; the server decides whether it may
 * be stored (`idNumberRule`), and it lives on the fiche only while pending.
 */
export const borneSubmitInputSchema = z.object({
  id: z.uuid().optional(),
  unitId: z.string().min(1),
  hostUserId: z.string().min(1).nullable().optional(),
  visitorName: z.string().trim().min(1).max(120),
  visitorPhone: borneVisitorPhoneSchema,
  purpose: spotArrivalPurposeSchema,
  idNumber: z.string().trim().min(1).max(32).optional(),
});
export type BorneSubmitInput = z.input<typeof borneSubmitInputSchema>;

/** The line above the submit button (spec §4). The link to the privacy
 *  notice sits next to it in the UI. */
export function borneConsentNotice(orgName: string): string {
  return `Vos données sont transmises à l'accueil de ${orgName} pour enregistrer votre visite, conformément à la loi 09-08.`;
}

/**
 * The « normalised name » of spec §5.1: case, accents, punctuation and word
 * order do not count. A visitor types « BENALI Karim » for an invitation that
 * says « Karim Bénali ».
 */
export function normalizePersonName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .sort()
    .join(" ");
}

export interface BorneMatchCandidate {
  guestId: string;
  /** The invitation's unit. The caller already restricts to the chosen unit;
   *  this function checks again so the rule holds on its own. */
  unitId: string | null;
  /** `invite.created_by_id`, the invitation's host. */
  hostUserId: string | null;
  guestName: string | null;
  guestPhone: string | null;
}

export interface BorneMatchInput {
  unitId: string;
  hostUserId: string | null;
  visitorName: string;
  /** E.164, as `borneSubmitInputSchema` outputs it. */
  visitorPhone: string;
}

/**
 * The expected-list suggestion, computed once at submit (spec §5.1):
 *   1. only invitations for the chosen unit, never another company's;
 *   2. a candidate matches on phone OR on normalised name;
 *   3. several matches + a chosen host → keep those that host invited;
 *   4. exactly one left → its guest id; zero or several → null.
 * A pointer only: `scan.record` re-checks eligibility at confirm time, and
 * « Arrivé » claims the fiche only if this stored match is the guest confirmed.
 */
export function matchExpectedGuest(
  candidates: readonly BorneMatchCandidate[],
  input: BorneMatchInput,
): string | null {
  const phone = parsePhone(input.visitorPhone);
  const name = normalizePersonName(input.visitorName);
  const seen = new Set<string>();
  let hits = candidates.filter((c) => {
    if (c.unitId !== input.unitId || seen.has(c.guestId)) return false;
    const byPhone =
      phone !== null &&
      c.guestPhone !== null &&
      parsePhone(c.guestPhone) === phone;
    const byName =
      name.length > 0 &&
      c.guestName !== null &&
      normalizePersonName(c.guestName) === name;
    if (!byPhone && !byName) return false;
    seen.add(c.guestId);
    return true;
  });
  if (hits.length > 1 && input.hostUserId) {
    hits = hits.filter((c) => c.hostUserId === input.hostUserId);
  }
  return hits.length === 1 ? (hits[0]?.guestId ?? null) : null;
}
